import React, { FC } from 'react';
import styled from 'styled-components';
import { SwiperCarousel, SwiperSlide } from '../Swiper';
import useContentful from '../../hooks/useContentful';
import Loader from '../Loader';
import { ContentfulArticle } from './Blog';

const MorePostsWrapper = styled.div`
  margin: 48px auto 0;

  @media (min-width: 1024px) {
    padding: 24px 0;
  }
`;

const SectionTitle = styled.p`
  margin: 0;
  font-size: clamp(1.5rem, 1.5vw, 3rem);
  font-weight: 600;
  margin-bottom: 16px;
  color: #000;
`;

const PostCard = styled.a`
  margin: 16px auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-align: center;
  text-decoration: none;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding-bottom: 56.25%;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: left;
  }
`;

const CardTitle = styled.h2`
  font-size: 18px;
  letter-spacing: -0.36px;
  margin: 16px 8px 0 0;
  line-height: 28px;
  color: rgba(0, 0, 0, 0.8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

interface MorePostsProps {
  blogUrl?: string;
  contentfulTagId?: string;
}

interface ContentfulQueryArgs {
  content_type: string;
  order: string[];
  limit: number;
  'metadata.tags.sys.id[in]'?: string;
}

const MorePosts: FC<MorePostsProps> = ({ blogUrl, contentfulTagId }) => {
  const queryArgs: ContentfulQueryArgs = {
    content_type: 'article',
    order: ['-sys.createdAt'],
    limit: 10
  };

  if (contentfulTagId) {
    queryArgs['metadata.tags.sys.id[in]'] = contentfulTagId;
  }

  const {contentfulData, isLoadingContentful} = useContentful(queryArgs);

  if (isLoadingContentful) {
    return <Loader />;
  }

  if (!contentfulData || contentfulData.items.length === 0) {
    return null; // No posts to show
  }

  const renderPostCard = (article: ContentfulArticle) => {
    const { title, slug, featuredMedia } = article.fields;

    return (
      <PostCard key={slug} href={`${blogUrl}/${slug}`}>
        {featuredMedia && (
          <ImageWrapper>
            <img src={`https:${featuredMedia.fields.file?.url}`} alt={title} />
          </ImageWrapper>
        )}
        <CardTitle title={title}>{title}</CardTitle>
      </PostCard>
    );
  };

  return (
    <MorePostsWrapper>
      <>
        <SectionTitle>More Posts</SectionTitle>
        <SwiperCarousel
          loop={true}
          spaceBetween={30}
          slidesPerView={1}
          breakpoints={{
            768: {
              slidesPerView: 2,
              spaceBetween: 10
            },
            1200: {
              slidesPerView: 3,
              spaceBetween: 20
            }
          }}
        >
          {contentfulData.items.map(article => (
            // @ts-ignore
            <SwiperSlide key={article.sys.id}>{renderPostCard(article)}</SwiperSlide>
          ))}
        </SwiperCarousel>
      </>
    </MorePostsWrapper>
  );
};

export default MorePosts;
